import React, { useEffect, useState } from "react";
import { Video, Plus, Trash2, Save, Upload, ArrowUp, ArrowDown, Home } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const HomepagePage = () => {
  const [heroUrl, setHeroUrl] = useState("");
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogoIdx, setUploadingLogoIdx] = useState(null);
  const [uploadingHero, setUploadingHero] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/site-config");
      setHeroUrl(r.data.hero_video_url || "");
      setLogos(r.data.client_logos || []);
    } catch {
      toast.error("Konnte Homepage-Config nicht laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/admin/uploads", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data.url; // /api/uploads/{uuid}.ext
  };

  const onHeroFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHero(true);
    try {
      const url = await uploadFile(file);
      // relative /api/uploads/... works when frontend is on same origin behind ingress
      const abs = `${process.env.REACT_APP_BACKEND_URL}${url}`;
      setHeroUrl(abs);
      toast.success("Video hochgeladen");
    } catch (err) {
      toast.error("Upload fehlgeschlagen: " + (err?.response?.data?.detail || err.message));
    } finally {
      setUploadingHero(false);
      e.target.value = "";
    }
  };

  const onLogoFile = async (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogoIdx(idx);
    try {
      const url = await uploadFile(file);
      const abs = `${process.env.REACT_APP_BACKEND_URL}${url}`;
      setLogos((prev) => prev.map((l, i) => (i === idx ? { ...l, src: abs } : l)));
      toast.success("Logo hochgeladen");
    } catch (err) {
      toast.error("Upload fehlgeschlagen: " + (err?.response?.data?.detail || err.message));
    } finally {
      setUploadingLogoIdx(null);
      e.target.value = "";
    }
  };

  const addLogo = () => {
    setLogos((prev) => [...prev, { id: `new-${Date.now()}`, name: "Neue Marke", src: "", bg: "light" }]);
  };
  const updateLogo = (idx, patch) => {
    setLogos((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const removeLogo = (idx) => {
    if (!window.confirm("Logo wirklich entfernen?")) return;
    setLogos((prev) => prev.filter((_, i) => i !== idx));
  };
  const moveLogo = (idx, dir) => {
    const nextIdx = idx + dir;
    if (nextIdx < 0 || nextIdx >= logos.length) return;
    const arr = [...logos];
    [arr[idx], arr[nextIdx]] = [arr[nextIdx], arr[idx]];
    setLogos(arr);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site-config", {
        hero_video_url: heroUrl,
        client_logos: logos.map(({ id, name, src, bg }) => ({
          id: id?.startsWith("new-") ? undefined : id,
          name,
          src,
          bg: bg === "dark" ? "dark" : "light",
        })),
      });
      toast.success("Homepage aktualisiert");
      await load();
    } catch (err) {
      toast.error("Speichern fehlgeschlagen: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="text-white">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-pink-400">
            <Home size={12} /> Backoffice
          </div>
          <h1 className="mt-2 font-display font-extrabold text-3xl md:text-4xl tracking-tight">
            Homepage-Inhalte
          </h1>
          <p className="mt-2 text-sm text-neutral-400">
            Hero-Video und Partner-Logos in der „Bereits gearbeitet für / mit"-Marquee pflegen.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving || loading}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white text-xs font-bold uppercase tracking-[0.12em] disabled:opacity-60"
          data-testid="homepage-save-button"
        >
          <Save size={14} /> {saving ? "Speichert…" : "Speichern"}
        </button>
      </div>

      {loading ? (
        <div className="text-neutral-400 text-sm">Laden…</div>
      ) : (
        <>
          {/* Hero Video */}
          <section className="mb-10 p-6 rounded-2xl bg-white/[0.04] border border-white/10">
            <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
              <Video size={16} className="text-pink-400" /> Hero-Showreel Video
            </h2>
            {heroUrl && (
              <video
                key={heroUrl}
                src={heroUrl}
                muted
                playsInline
                loop
                autoPlay
                className="w-full max-w-md aspect-[9/16] rounded-2xl object-contain bg-black mb-4"
                data-testid="homepage-hero-preview"
              />
            )}
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <input
                type="text"
                value={heroUrl}
                onChange={(e) => setHeroUrl(e.target.value)}
                placeholder="Video URL (mp4/webm)"
                className="flex-1 px-4 py-3 rounded-xl bg-black/40 border border-white/10 outline-none focus:border-pink-400/60 text-sm"
                data-testid="homepage-hero-url"
              />
              <label className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/15 cursor-pointer text-xs font-bold uppercase tracking-[0.14em]">
                <Upload size={14} />
                {uploadingHero ? "Lädt…" : "Neu hochladen"}
                <input
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={onHeroFile}
                  data-testid="homepage-hero-file-input"
                />
              </label>
            </div>
          </section>

          {/* Logo Marquee */}
          <section className="p-6 rounded-2xl bg-white/[0.04] border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Partner-Logos ({logos.length})</h2>
              <button
                onClick={addLogo}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-neutral-950 text-xs font-bold uppercase tracking-[0.12em] hover:bg-pink-100"
                data-testid="homepage-add-logo"
              >
                <Plus size={14} /> Neu
              </button>
            </div>
            <div className="space-y-3">
              {logos.map((logo, idx) => (
                <div
                  key={logo.id || idx}
                  className="grid grid-cols-1 md:grid-cols-[80px_1fr_140px_auto] gap-3 items-center p-3 rounded-xl bg-black/30 border border-white/5"
                  data-testid={`homepage-logo-row-${idx}`}
                >
                  <div className={`w-16 h-16 rounded-lg flex items-center justify-center overflow-hidden ${logo.bg === "dark" ? "bg-neutral-900" : "bg-white"}`}>
                    {logo.src ? (
                      <img src={logo.src} alt={logo.name} className="max-w-[80%] max-h-[80%] object-contain" />
                    ) : (
                      <span className="text-[10px] text-neutral-500">kein Bild</span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      value={logo.name}
                      onChange={(e) => updateLogo(idx, { name: e.target.value })}
                      placeholder="Markenname"
                      className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 outline-none focus:border-pink-400/60 text-sm"
                    />
                    <input
                      value={logo.src}
                      onChange={(e) => updateLogo(idx, { src: e.target.value })}
                      placeholder="Logo URL"
                      className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 outline-none focus:border-pink-400/60 text-sm"
                    />
                  </div>
                  <select
                    value={logo.bg}
                    onChange={(e) => updateLogo(idx, { bg: e.target.value })}
                    className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 outline-none text-sm"
                  >
                    <option value="light">Weißer BG</option>
                    <option value="dark">Dunkler BG</option>
                  </select>
                  <div className="flex items-center gap-1 justify-end">
                    <label className="p-2 rounded-lg bg-white/10 hover:bg-white/15 cursor-pointer" title="Bild ersetzen">
                      <Upload size={13} />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => onLogoFile(idx, e)}
                      />
                    </label>
                    <button
                      onClick={() => moveLogo(idx, -1)}
                      disabled={idx === 0}
                      className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-30"
                      title="Nach oben"
                    >
                      <ArrowUp size={13} />
                    </button>
                    <button
                      onClick={() => moveLogo(idx, 1)}
                      disabled={idx === logos.length - 1}
                      className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-30"
                      title="Nach unten"
                    >
                      <ArrowDown size={13} />
                    </button>
                    <button
                      onClick={() => removeLogo(idx)}
                      className="p-2 rounded-lg text-red-400 hover:bg-red-500/10"
                      title="Löschen"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                  {uploadingLogoIdx === idx && (
                    <div className="col-span-full text-[11px] text-pink-300">Lädt hoch…</div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default HomepagePage;
